import React, { useState } from "react";
import { FleetrifyModal } from "../../../Modal/modal";

interface Props {
    open: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => void;
    planName: string;
    companySubscriptionId?: string;
    loading?: boolean;
}

const reasons = [
    "Too expensive",
    "Missing a key feature",
    "Difficult to use",
    "No longer need Fleetrify / Business closed",
    "Switched to a competitor",
    "Poor customer support",
    "Other",
];

const CancelSubscriptionModal: React.FC<Props> = ({
    open,
    onClose,
    onConfirm,
    planName,
    companySubscriptionId,
    loading,
}) => {
    const [selectedReason, setSelectedReason] = useState("");
    const [otherReason, setOtherReason] = useState("");
    const [showConfirmation, setShowConfirmation] = useState(false);

    const finalReason =
        selectedReason === "Other" ? otherReason.trim() : selectedReason;

    const handleConfirm = () => {
        // Show confirmation modal instead of directly confirming
        setShowConfirmation(true);
    };

    const handleConfirmCancelation = () => {
        // 🔍 DEBUG LOG
        console.log("🟡 Company subscription ID (from modal):", companySubscriptionId);
        console.log("🟡 Cancel reason (from modal):", finalReason);
        onConfirm(finalReason);
    };

    const handleBackFromConfirmation = () => {
        // Go back to the previous modal
        setShowConfirmation(false);
    };

    return (
        <>
            {/* Main Cancel Reason Modal */}
            <FleetrifyModal
                isOpen={open && !showConfirmation}
                onClose={onClose}
                title="Help us improve Fleetrify"
            >
                <p className="text-sm mt-5 text-gray-900">
                    We're constantly working to make Fleetrify better. Please tell us why you're cancelling{" "}
                    <span className="font-medium">{planName}</span>.
                </p>
                <p className="text-sm text-gray-900">Select all that apply:</p>

                {reasons.map((reason) => (
                    <label
                        key={reason}
                        className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer"
                    >
                        <input
                            type="radio"
                            name="cancelReason"
                            value={reason}
                            checked={selectedReason === reason}
                            onChange={() => setSelectedReason(reason)}
                            className="form-radio accent-blue-800 w-5 h-5"
                        />
                        {reason}
                    </label>
                ))}

                {selectedReason === "Other" && (
                    <textarea
                        className="w-full border border-gray-400 rounded-lg p-2 text-sm mt-2 focus:outline-none"
                        cols={30}
                        placeholder="Please specify"
                        value={otherReason}
                        onChange={(e) => setOtherReason(e.target.value)}
                    />
                )}

                <div className="flex flex-col sm:flex-row sm:justify-end gap-3 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg border border-red-600 text-red-600 text-sm hover:bg-red-50"
                    >
                        Go Back
                    </button>

                    <button
                        disabled={!finalReason || loading}
                        onClick={handleConfirm}
                        className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50"
                    >
                        {loading ? "Cancelling..." : "Cancel Anyway"}
                    </button>
                </div>
            </FleetrifyModal>

            {/* Confirmation Modal */}
            <FleetrifyModal
                isOpen={open && showConfirmation}
                onClose={handleBackFromConfirmation}
                title="Confirm Cancellation"
            >
                <p className="text-sm mt-5 text-gray-900">
                    Are you sure you want to cancel <span className="font-medium">{planName}</span> subscription?
                </p>
                <p className="text-sm text-gray-600 mt-3">
                    This action cannot be undone. Your subscription will be cancelled immediately.
                </p>

                <div className="flex flex-col sm:flex-row sm:justify-end gap-3 mt-6">
                    <button
                        onClick={handleBackFromConfirmation}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
                    >
                        Back
                    </button>

                    <button
                        disabled={loading}
                        onClick={handleConfirmCancelation}
                        className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50"
                    >
                        {loading ? "Cancelling..." : "Continue"}
                    </button>
                </div>
            </FleetrifyModal>
        </>
    );
};

export default CancelSubscriptionModal;
